// ============================================================================
// PAYMENTS / KYC HOLD
// ----------------------------------------------------------------------------
// One admin switch (Admin > Settings > Payments & KYC Hold) that
//   - shows every player a persistent notice until it is turned off, and
//   - blocks card purchases, ID verification and (optionally) redemptions while on.
// Stored at system/paymentsHold (public read, admin write). The server
// enforces the same flags on its endpoints; this is the client mirror.
// ============================================================================
import { useEffect, useState } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from './firebase';

export interface PaymentsHold {
  enabled: boolean;
  message: string;
  blockPurchases: boolean;
  blockKyc: boolean;
  blockCashOuts: boolean;
  /** Apple App Store purchases are independent of the card processor; off by default. */
  blockIap: boolean;
}

export const DEFAULT_PAYMENTS_HOLD_MESSAGE =
  'We are changing our card payment and identity verification providers. Please hold off on card purchases and ID verification until this notice is cleared. Purchases through the App Store still work, and your balances and games are unaffected.';

export const PAYMENTS_HOLD_OFF: PaymentsHold = {
  enabled: false, message: DEFAULT_PAYMENTS_HOLD_MESSAGE, blockPurchases: true, blockKyc: true, blockCashOuts: false, blockIap: false,
};

const parse = (data: any): PaymentsHold => ({
  enabled: data?.enabled === true,
  message: (data?.message && String(data.message).trim()) || DEFAULT_PAYMENTS_HOLD_MESSAGE,
  blockPurchases: data?.blockPurchases !== false,
  blockKyc: data?.blockKyc !== false,
  blockCashOuts: data?.blockCashOuts === true,
  blockIap: data?.blockIap === true,
});

let current: PaymentsHold = PAYMENTS_HOLD_OFF;
const listeners = new Set<(h: PaymentsHold) => void>();
let started = false;

function start() {
  if (started) return;
  started = true;
  onSnapshot(doc(db, 'system', 'paymentsHold'), (snap) => {
    current = snap.exists() ? parse(snap.data()) : PAYMENTS_HOLD_OFF;
    listeners.forEach((l) => l(current));
  }, (err) => {
    console.warn('[paymentsHold] listener error:', err);
  });
}

/** Subscribe from anywhere (non-React code). */
export function subscribePaymentsHold(cb: (h: PaymentsHold) => void): () => void {
  start();
  listeners.add(cb);
  cb(current);
  return () => { listeners.delete(cb); };
}

export function getPaymentsHold(): PaymentsHold { start(); return current; }

/** React hook: live hold state. */
export function usePaymentsHold(): PaymentsHold {
  const [hold, setHold] = useState<PaymentsHold>(current);
  useEffect(() => subscribePaymentsHold(setHold), []);
  return hold;
}

/** True when a server call came back with the hold's 503. */
export function isPaymentsHoldError(err: any): boolean {
  const msg = String(err?.message || err || '');
  return err?.code === 'payments_hold' || /hold off on (card )?(purchases|deposits)|changing (our )?payment/i.test(msg);
}
